/**
 * REGISTRE DE MODULES — découverte structurelle par import.meta.glob.
 * Aucun import manuel : déposer un dossier `modules/<id>/module.ts` suffit.
 */
import type { NavGroupId, ProfilMenu, Role } from "@geceram/shared";
import { NAV_GROUPS, modulesVisibles } from "@geceram/shared";
import type { ModuleDef, Widget360, WidgetZone } from "./module-contract";
import { canAccess, widgetAllowed } from "./module-contract";

const found = import.meta.glob<{ default: ModuleDef }>("../modules/*/module.ts", {
  eager: true,
});

function isEnabled(mod: ModuleDef): boolean {
  if (!mod.featureFlag) return true;
  return (import.meta.env as Record<string, unknown>)[mod.featureFlag] === "true";
}

/**
 * TOUS les modules présents sur le disque, drapeau ÉTEINT COMPRIS.
 *
 * N'est PAS ce que l'application monte — voir `registry` juste en dessous. Cette
 * liste existe pour que les invariants structurels du dépôt (ids uniques,
 * collision de chemins, navGroup valide, rolesRequis ⊆ ROLES, anti-hardcode du
 * noyau) portent sur ce que le dépôt CONTIENT, et non sur ce qu'un interrupteur
 * laisse voir aujourd'hui. Sans elle, un module éteint n'était jamais regardé :
 * ses défauts n'apparaissaient qu'au jour de l'allumage.
 */
export const MODULES_DECOUVERTS: readonly ModuleDef[] = Object.values(found)
  .map((m) => m.default)
  // Ordre logique = ordre visuel de la nav : groupe d'abord, navOrder ensuite.
  // (Deux modules à navOrder égal dans des groupes différents ne doivent jamais
  // dépendre de l'ordre alphabétique du glob — vécu : /admin devenait l'accueil.)
  .sort(
    (a, b) =>
      NAV_GROUPS.indexOf(a.navGroup) - NAV_GROUPS.indexOf(b.navGroup) || a.navOrder - b.navOrder,
  );

/**
 * Les modules ALLUMÉS — la seule liste que l'application monte. Filtrer APRÈS le
 * tri rend exactement l'ordre d'avant : un filtre ne réordonne pas.
 */
export const registry: readonly ModuleDef[] = MODULES_DECOUVERTS.filter(isEnabled);

/**
 * Routes accessibles au rôle courant — fonction PURE (registre en paramètre),
 * pour que le test d'invariants puisse l'exercer sur un registre factice sans
 * dépendre d'`import.meta.glob`. `sansNav` n'y change rien : une route reste
 * montée même quand son module n'a pas de lien de sidebar (fiche de travail).
 * `routesFor` ci-dessous l'applique au registre réel (dérivation unique
 * consommée par App).
 */
export function routesForModules(modules: readonly ModuleDef[], roles: readonly Role[]) {
  return modules.filter((m) => canAccess(m, roles)).flatMap((m) => [...m.routes]);
}

export function routesFor(roles: readonly Role[]) {
  return routesForModules(registry, roles);
}

/**
 * Routes servies HORS SESSION (É8) — aucun rôle n'intervient, par définition.
 *
 * Elles suivent le drapeau du module comme les autres : un module éteint au
 * build n'expose rien du tout. C'est un drapeau de CONSTRUCTION (`VITE_`), pas
 * un interrupteur d'exploitation — l'éteindre demande un redéploiement, il ne
 * peut donc pas couper en pleine journée l'accès d'un cabinet à qui un lien
 * vient d'être remis. La route du BFF, elle, ignore délibérément le drapeau
 * pour la même raison.
 */
export function routesPubliques(): ReadonlyArray<{ path: string; component: ModuleDef["routes"][number]["component"] }> {
  return registry.flatMap((m) => [...(m.routesPubliques ?? [])]);
}

/**
 * Arbre de navigation groupé — fonction PURE, même raison que
 * `routesForModules`. Un module `sansNav` est monté (sa route existe via
 * `routesFor`) mais n'apparaît jamais ici : c'est le seul endroit qui le
 * filtre. `navTreeFor` ci-dessous l'applique au registre réel (dérivation
 * unique consommée par Shell).
 */
export function arbreDeNav(
  modules: readonly ModuleDef[],
  roles: readonly Role[],
): ReadonlyArray<{ group: NavGroupId; modules: ModuleDef[] }> {
  const visible = modules.filter((m) => canAccess(m, roles) && !m.sansNav);
  return NAV_GROUPS.map((group) => ({
    group,
    modules: visible.filter((m) => m.navGroup === group),
  })).filter((g) => g.modules.length > 0);
}

export function navTreeFor(roles: readonly Role[]): ReadonlyArray<{ group: NavGroupId; modules: ModuleDef[] }> {
  return arbreDeNav(registry, roles);
}

export interface NavReduite {
  groupes: ReadonlyArray<{ libelle: string; modules: ModuleDef[] }>;
  /** ce que la personne peut ouvrir mais que la maquette range sous « Plus… » */
  plus: ModuleDef[];
}

/**
 * Le menu RÉDUIT d'un profil (décision Dove 15/09) — fonction PURE, même raison
 * que `arbreDeNav`. Les groupes viennent du profil, pas de `NAV_GROUPS` ; « Plus »
 * garde l'ordre du registre. Un id du profil que le rôle ne peut pas ouvrir est
 * sauté : le profil ne DONNE aucun droit, il range.
 */
export function arbreReduit(modules: readonly ModuleDef[], roles: readonly Role[], profil: ProfilMenu): NavReduite {
  const ouvrables = modules.filter((m) => canAccess(m, roles) && !m.sansNav);
  const parId = new Map(ouvrables.map((m) => [m.id, m]));
  const visibles = new Set(modulesVisibles(profil));
  const groupes = profil.groupes
    .map((g) => ({ libelle: g.libelle, modules: g.modules.map((id) => parId.get(id)).filter((m): m is ModuleDef => m !== undefined) }))
    .filter((g) => g.modules.length > 0);
  const plus = ouvrables.filter((m) => !visibles.has(m.id) && !profil.exclus.includes(m.id));
  return { groupes, plus };
}

export function navReduiteFor(roles: readonly Role[], profil: ProfilMenu): NavReduite {
  return arbreReduit(registry, roles, profil);
}

/** Widgets d'une zone de la fiche 360 (dérivation unique de Fiche360Layout). */
export function widgetsFor(zone: WidgetZone, roles: readonly Role[]): ReadonlyArray<Widget360> {
  return registry
    .flatMap((m) => (m.widgets360 ?? []).map((w) => ({ mod: m, w })))
    .filter(({ mod, w }) => w.zone === zone && widgetAllowed(mod, w, roles))
    .sort((a, b) => a.w.order - b.w.order)
    .map(({ w }) => w);
}
